<div class="panel-shell">
  <header class="panel-header primary">
    <div class="panel-title text-bold flex-row-start-center">
      <mat-icon class="s-24 mr-8">{{ getIcon("NOTIFICATIONS") }}</mat-icon>
      {{ "NOTIFICATION_CENTER.TITLE" | translate }}
    </div>
    <button
      mat-icon-button
      type="button"
      class="panel-close-btn"
      (click)="close()"
      [attr.aria-label]="'NOTIFICATION_CENTER.CLOSE' | translate"
    >
      <mat-icon>{{ getIcon("CLOSE") }}</mat-icon>
    </button>
  </header>

  <!-- HEAD: filters + global actions (never scrolls) -->
  <div class="bell-head">
    <mat-button-toggle-group
      class="bell-filters"
      [value]="filter"
      (change)="onFilterChange($event.value)"
      [attr.aria-label]="'NOTIFICATION_CENTER.FILTER_ARIA' | translate"
      hideSingleSelectionIndicator
    >
      <mat-button-toggle *ngFor="let item of filters" [value]="item.key">
        {{ item.labelKey | translate }}
        <span
          *ngIf="item.key === 'NEW' && unreadCount > 0"
          class="bell-filter-count"
          >{{ unreadCount > 99 ? "99+" : unreadCount }}</span
        >
      </mat-button-toggle>
    </mat-button-toggle-group>

    <div class="bell-head-actions">
      <button
        mat-button
        type="button"
        class="bell-head-action"
        [disabled]="markingAllRead || unreadCount === 0"
        (click)="onMarkAllRead()"
      >
        <mat-icon class="s-20">{{ getIcon("MARK_ALL_READ") }}</mat-icon>
        {{ "NOTIFICATION_CENTER.MARK_ALL_READ" | translate }}
      </button>

      <button
        mat-button
        type="button"
        class="bell-head-action"
        (click)="onPreferences()"
      >
        <mat-icon class="s-20">{{
          getIcon("NOTIFICATION_PREFERENCES")
        }}</mat-icon>
        {{ "NOTIFICATION_CENTER.MY_PREFERENCES" | translate }}
      </button>
    </div>
  </div>
  <!-- / HEAD -->

  <div class="panel-body">
    <!-- ERROR -->
    <div *ngIf="errorState" class="bell-state" role="alert">
      <mat-icon class="s-40 bell-state-icon bell-state-icon--error">{{
        getIcon("WARNING")
      }}</mat-icon>
      <div class="bell-state-title">
        {{ "NOTIFICATION_CENTER.ERROR_TITLE" | translate }}
      </div>
      <div class="bell-state-text">
        {{ "NOTIFICATION_CENTER.ERROR_MESSAGE" | translate }}
      </div>
      <button
        mat-raised-button
        type="button"
        color="accent"
        class="mt-12"
        (click)="reload()"
      >
        {{ "NOTIFICATION_CENTER.RETRY" | translate }}
      </button>
    </div>
    <!-- / ERROR -->

    <!-- LOADING -->
    <div *ngIf="loading" class="bell-state" role="status">
      <mat-spinner [diameter]="32"></mat-spinner>
      <div class="bell-state-text mt-8">
        {{ "NOTIFICATION_CENTER.LOADING" | translate }}
      </div>
    </div>
    <!-- / LOADING -->

    <!-- EMPTY -->
    <div *ngIf="isEmpty" class="bell-state" role="status">
      <mat-icon class="s-40 bell-state-icon">{{
        getIcon("NOTIFICATIONS")
      }}</mat-icon>
      <div class="bell-state-title">{{ emptyTitleKey | translate }}</div>
      <div class="bell-state-text">{{ emptyTextKey | translate }}</div>
    </div>
    <!-- / EMPTY -->

    <!-- LIST -->
    <ul
      *ngIf="!loading && !errorState && rows.length > 0"
      class="bell-list"
      [attr.aria-label]="'NOTIFICATION_CENTER.LIST_ARIA' | translate"
    >
      <li
        *ngFor="let row of rows; trackBy: trackByUuid"
        class="bell-row"
        [ngClass]="severityClass(row.notification)"
        [class.bell-row--unread]="!row.notification.is_read"
        [class.bell-row--archived]="row.notification.is_archived"
      >
        <div class="bell-row-icon">
          <mat-icon class="s-24" aria-hidden="true">{{ row.icon }}</mat-icon>
          <span class="cdk-visually-hidden">{{
            severityLabelKey(row.notification) | translate
          }}</span>
        </div>

        <div class="bell-row-main">
          <button
            *ngIf="row.canOpen"
            type="button"
            class="bell-row-title bell-row-title--link"
            (click)="onOpen(row)"
          >
            {{ row.notification.title }}
          </button>
          <div *ngIf="!row.canOpen" class="bell-row-title">
            {{ row.notification.title }}
          </div>

          <div *ngIf="row.notification.body" class="bell-row-body">
            {{ row.notification.body }}
          </div>

          <div class="bell-row-meta">
            <span
              *ngIf="!row.notification.is_read"
              class="bell-row-state bell-row-state--unread"
            >
              <span class="bell-unread-dot" aria-hidden="true"></span>
              {{ "NOTIFICATION_CENTER.UNREAD" | translate }}
            </span>
            <span *ngIf="row.notification.is_archived" class="bell-row-state">{{
              "NOTIFICATION_CENTER.ARCHIVED" | translate
            }}</span>
            <span [matTooltip]="row.fullDate">{{ row.relativeDate }}</span>
          </div>
        </div>

        <div class="bell-row-actions">
          <!-- Open -->
          <button
            *ngIf="row.canOpen"
            mat-icon-button
            type="button"
            [matTooltip]="'NOTIFICATION_CENTER.OPEN' | translate"
            [attr.aria-label]="'NOTIFICATION_CENTER.OPEN' | translate"
            (click)="onOpen(row)"
          >
            <mat-icon class="s-20">{{
              getIcon(
                row.notification.action_type === "EXTERNAL_URL"
                  ? "NEW_TAB"
                  : "ARROW_CIRCLE_RIGHT"
              )
            }}</mat-icon>
          </button>

          <!-- Mark read -->
          <button
            *ngIf="!row.notification.is_read && !row.notification.is_archived"
            mat-icon-button
            type="button"
            [disabled]="busyUuid === row.notification.uuid"
            [matTooltip]="'NOTIFICATION_CENTER.MARK_READ' | translate"
            [attr.aria-label]="'NOTIFICATION_CENTER.MARK_READ' | translate"
            (click)="onChange(row, 'read')"
          >
            <mat-icon class="s-20">{{ getIcon("MARK_READ") }}</mat-icon>
          </button>

          <!-- Mark unread again -->
          <button
            *ngIf="row.notification.is_read && !row.notification.is_archived"
            mat-icon-button
            type="button"
            [disabled]="busyUuid === row.notification.uuid"
            [matTooltip]="'NOTIFICATION_CENTER.MARK_UNREAD' | translate"
            [attr.aria-label]="'NOTIFICATION_CENTER.MARK_UNREAD' | translate"
            (click)="onChange(row, 'unread')"
          >
            <mat-icon class="s-20">{{ getIcon("MARK_UNREAD") }}</mat-icon>
          </button>

          <!-- Archive -->
          <button
            *ngIf="!row.notification.is_archived"
            mat-icon-button
            type="button"
            [disabled]="busyUuid === row.notification.uuid"
            [matTooltip]="'NOTIFICATION_CENTER.ARCHIVE' | translate"
            [attr.aria-label]="'NOTIFICATION_CENTER.ARCHIVE' | translate"
            (click)="onChange(row, 'archive')"
          >
            <mat-icon class="s-20">{{ getIcon("ARCHIVE") }}</mat-icon>
          </button>

          <!-- Unarchive -->
          <button
            *ngIf="row.notification.is_archived"
            mat-icon-button
            type="button"
            [disabled]="busyUuid === row.notification.uuid"
            [matTooltip]="'NOTIFICATION_CENTER.UNARCHIVE' | translate"
            [attr.aria-label]="'NOTIFICATION_CENTER.UNARCHIVE' | translate"
            (click)="onChange(row, 'unarchive')"
          >
            <mat-icon class="s-20">{{ getIcon("UNARCHIVE") }}</mat-icon>
          </button>
        </div>
      </li>
    </ul>
    <!-- / LIST -->

    <!-- SHOW MORE -->
    <div *ngIf="!loading && !errorState && rows.length > 0" class="bell-more">
      <div class="bell-more-count">
        {{
          "NOTIFICATION_CENTER.SHOWN_COUNT"
            | translate: { count: rows.length, total: total }
        }}
      </div>
      <button
        *ngIf="hasMore"
        mat-stroked-button
        type="button"
        [disabled]="loadingMore"
        (click)="onShowMore()"
      >
        <mat-spinner
          *ngIf="loadingMore"
          [diameter]="16"
          class="bell-more-spinner"
        ></mat-spinner>
        {{ "NOTIFICATION_CENTER.SHOW_MORE" | translate }}
      </button>
    </div>
    <!-- / SHOW MORE -->
  </div>

  <footer class="panel-footer gt2-buttons-theme flex-row-center-center">
    <button
      mat-raised-button
      type="button"
      color="primary"
      class="mr-8 ml-8 mt-4 mb-4"
      (click)="close()"
    >
      {{ "NOTIFICATION_CENTER.CLOSE" | translate }}
    </button>
  </footer>
</div>
